<script lang="ts">
	import CourseRegistrationModal from "@components/CourseRegistrationModal.svelte";
	import SubscriptionConfiguratorModal from "@components/SubscriptionConfiguratorModal.svelte";
	import { ButtonSize } from "@components/common/ButtonSize.js";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Link from "@components/common/Link.svelte";
	import SideModal from "@components/common/SideModal.svelte";
	import { getTranslate } from "@utils/getTranslate.js";
	import cofeeBeanIcon from "@core/assets/icons/cofee-bean.svg?raw";
	import { ButtonVariant } from "@core/schema/ButtonVariant.js";
	import { CategoryType } from "@core/schema/CategoryType.js";
	import { DeliveryType } from "@core/schema/DeliveryType.js";
	import { getCartPath } from "@core/schema/paths/getCartPath.js";
	import { getCategoryPath } from "@core/schema/paths/getCategoryPath.js";
	import { getShippingStartsAtOfProducts } from "@core/schema/utils/getShippingStartsAtOfProducts.js";
	import { createReactiveBreakpoint } from "@core/utils/createReactiveBreakpoint.js";
	import { formatPrice } from "@core/utils/formatPrice.js";
	import { toDateMonthYearFormat } from "@core/utils/toDateMonthYearFormat.js";
	import { Cart } from "../../cart/Cart.svelte.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import { selectedCurrency } from "../../state/selectedCurrency.svelte.js";
	import CartClosestBenefit from "./CartClosestBenefit.svelte";
	import translations from "./CartOverviewModal.translations.json" with { type: "json" };
	import CartOverviewProduct from "./CartOverviewProduct.svelte";
	import CartOverviewPurchasedCoupon from "./CartOverviewPurchasedCoupon.svelte";

	const { tenant } = getGlobalTemplateProps();
	const translate = getTranslate(translations);

	const isDesktop = createReactiveBreakpoint(">2xl");
	const preorderShippingStartsAt = $derived(getShippingStartsAtOfProducts(Cart.productsInOrder, Date.now()));
	const preorderNoteKey = $derived(
		Cart.state?.order.deliveryType === DeliveryType.PersonalPickup ? "preorderPickupNote" : "preorderShippingNote",
	);
	const subtotal = $derived(
		Cart.orderProducts.reduce((sum, { info, quantity }) => sum + info.price.withTax * quantity, 0) +
			Cart.orderPurchasedCoupons.reduce((sum, { amount, quantity }) => sum + amount * quantity, 0),
	);
	const courseOrderPrice = $derived(
		formatPrice(Cart.courseOrderPrice.withTax, selectedCurrency.current ?? tenant.defaultCurrency, tenant.locale, true),
	);
</script>

{#if Cart.state?.hasSubscription}
	<SubscriptionConfiguratorModal bind:show={Cart.showModal} />
{:else if Cart.state?.hasCourseOrder && Cart.state.courseOrder.term}
	<CourseRegistrationModal bind:show={Cart.showModal} term={Cart.state.courseOrder.term} price={courseOrderPrice} />
{:else}
	<SideModal
		class="xs:max-w-[30rem] md:max-w-[36rem] lg:max-w-full"
		heading={translate("cartTitle")}
		bind:show={Cart.showModal}
		fullContentWidth
	>
		<div class="h-full flex flex-col">
			<div class="grow shrink px-4 md:px-12 overflow-y-auto flex flex-col gap-8 sm:gap-4 pb-10">
				{#if !Cart.isEmpty}
					{#each Cart.orderProducts as orderProduct (orderProduct.id)}
						<CartOverviewProduct {orderProduct} />
					{/each}
					{#each Cart.orderPurchasedCoupons as orderPurchasedCoupon (orderPurchasedCoupon.id)}
						<CartOverviewPurchasedCoupon {orderPurchasedCoupon} />
					{/each}
				{:else}
					<div class="text-center mt-10 items-center flex flex-col gap-4">
						<Icon icon={cofeeBeanIcon} class="size-12 text-fcrbrown" />
						<span class="font-antipol text-fcrdarkbrown font-medium text-6">
							<Formatted text={translate("cartEmpty")} />
						</span>
						<Link
							href={getCategoryPath(tenant.locale, CategoryType.Coffee)}
							size={ButtonSize.Medium}
							variant={ButtonVariant.Dark}
							class="mt-4"
						>
							<Formatted text={translate("currentOffer")} />
						</Link>
					</div>
				{/if}
			</div>
			<div
				class="grow-0 shrink-0 bg-fcrbeige px-4 md:px-12 pb-[max(env(safe-area-inset-bottom,0),0.5rem)] xl:pb-4 2xl:pb-6 sticky md:static w-full z-10 bottom-0"
			>
				{#if preorderShippingStartsAt}
					<p class="text-fcrgray text-sm pt-4">
						<Formatted
							text={translate(preorderNoteKey, {
								shippingDate: toDateMonthYearFormat(preorderShippingStartsAt, tenant.locale),
							})}
						/>
					</p>
				{/if}
				<div
					class="flex justify-between items-center text-fcrdarkgray font-medium py-4 2xl:py-6 border-b border-fcrlightgray"
				>
					<div class="text-4">
						<Formatted text={translate("subtotal")} />
					</div>
					<div class="text-5.5">
						{#if selectedCurrency.current !== undefined}
							<Formatted text={formatPrice(subtotal, selectedCurrency.current, tenant.locale, true)} />
						{/if}
					</div>
				</div>
				{#if !Cart.isVoucherOnlyCart}
					<CartClosestBenefit />
				{/if}
				{#if !Cart.isEmpty}
					<Link
						href={getCartPath(tenant)}
						size={isDesktop.current ? ButtonSize.Large : ButtonSize.Medium}
						variant={ButtonVariant.Dark}
						class="w-full mt-4 xl:mt-7 2xl:mt-10"
					>
						<Formatted text={translate("continueToCheckout")} />
					</Link>
				{/if}
			</div>
		</div>
	</SideModal>
{/if}
